import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal as MuiModal, Paper, Typography } from "@mui/material";
import type { ModalProps } from "@mui/material/Modal";
import type { ReactNode } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks.ts";
import { removeCredentials, selectUser } from "features/auth/authSlice.ts";
import { removePermissions } from "features/permissions/permissionsSlice.ts";
import modalStyles from "components/organisms/Modal/modal.module.css";
import {
  SESSION_TIMEOUT_MINUTES,
  SESSION_TIMEOUT_WARNING_SECONDS,
} from "../../../config/sessionTimeout.ts";

interface SessionTimeoutHandlerProps {
  children: ReactNode;
  timeoutMinutes?: number;
  warningSeconds?: number;
}

const DEFAULT_TIMEOUT_MINUTES = SESSION_TIMEOUT_MINUTES;
const DEFAULT_WARNING_SECONDS = SESSION_TIMEOUT_WARNING_SECONDS;

type TimerRef = number | ReturnType<typeof setTimeout> | null;
type IntervalRef = number | ReturnType<typeof setInterval> | null;

const SessionTimeoutHandler = (props: SessionTimeoutHandlerProps) => {
  const {
    children,
    timeoutMinutes = DEFAULT_TIMEOUT_MINUTES,
    warningSeconds = DEFAULT_WARNING_SECONDS,
  } = props;
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  const warningTimeoutRef = useRef<TimerRef>(null);
  const logoutTimeoutRef = useRef<TimerRef>(null);
  const countdownIntervalRef = useRef<IntervalRef>(null);

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const isWarningOpenRef = useRef(isWarningOpen);
  const [countdown, setCountdown] = useState(warningSeconds);

  const warningDurationMs = useMemo(() => warningSeconds * 1000, [warningSeconds]);
  const timeoutDurationMs = useMemo(
    () => timeoutMinutes * 60 * 1000,
    [timeoutMinutes],
  );

  const clearTimers = useCallback(() => {
    if (warningTimeoutRef.current !== null) {
      window.clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current !== null) {
      window.clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const completeSignOut = useCallback(() => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("permissions");
    dispatch(removeCredentials());
    dispatch(removePermissions());
    window.location.assign("/");
  }, [dispatch]);

  const handleLogout = useCallback(() => {
    clearTimers();
    setIsWarningOpen(false);
    isWarningOpenRef.current = false;
    completeSignOut();
  }, [clearTimers, completeSignOut]);

  const startWarningCountdown = useCallback(() => {
    setIsWarningOpen(true);
    isWarningOpenRef.current = true;
    setCountdown(warningSeconds);

    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = window.setInterval(() => {
      setCountdown((previous) => {
        if (previous <= 1) {
          if (countdownIntervalRef.current !== null) {
            window.clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          handleLogout();
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  }, [handleLogout, warningSeconds]);

  const startTimers = useCallback(() => {
    if (!user) return;

    clearTimers();
    setIsWarningOpen(false);
    isWarningOpenRef.current = false;
    setCountdown(warningSeconds);

    warningTimeoutRef.current = window.setTimeout(
      startWarningCountdown,
      Math.max(timeoutDurationMs - warningDurationMs, 0),
    );
    logoutTimeoutRef.current = window.setTimeout(handleLogout, timeoutDurationMs);
  }, [
    clearTimers,
    handleLogout,
    startWarningCountdown,
    timeoutDurationMs,
    user,
    warningDurationMs,
    warningSeconds,
  ]);

  const handleStayLoggedIn = useCallback(() => {
    startTimers();
  }, [startTimers]);

  const handleModalClose = useCallback<NonNullable<ModalProps["onClose"]>>(
    (_, reason) => {
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        return;
      }

      handleLogout();
    },
    [handleLogout],
  );

  useEffect(() => {
    if (!user) {
      clearTimers();
      setIsWarningOpen(false);
      isWarningOpenRef.current = false;
      return;
    }

    startTimers();

    const activityEvents: Array<keyof DocumentEventMap> = [
      "click",
      "keydown",
      "mousedown",
      "mousemove",
      "scroll",
      "touchstart",
    ];

    const resetOnActivity = () => {
      if (isWarningOpenRef.current) {
        return;
      }
      startTimers();
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, resetOnActivity);
    });

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, resetOnActivity);
      });
      clearTimers();
      setIsWarningOpen(false);
      isWarningOpenRef.current = false;
    };
  }, [clearTimers, startTimers, user]);

  useEffect(() => {
    isWarningOpenRef.current = isWarningOpen;
  }, [isWarningOpen]);

  return (
    <>
      {children}
      <MuiModal
        className={modalStyles.modal}
        open={isWarningOpen}
        onClose={handleModalClose}
        aria-labelledby="session-timeout-title"
      >
        <Paper elevation={7} className={modalStyles.modalContent}>
          <Typography id="session-timeout-title" variant="h5">
            Session Expiring Soon
          </Typography>
          <Typography variant="body1" textAlign="center">
            You will be signed out in {countdown} seconds due to inactivity.
          </Typography>
          <Typography variant="body1" textAlign="center">
            Would you like to stay signed in?
          </Typography>
          <div className={modalStyles.buttons}>
            <Button color="primary" onClick={handleStayLoggedIn} variant="contained">
              Stay Logged In
            </Button>
            <Button color="secondary" onClick={handleLogout} variant="contained">
              Log Out
            </Button>
          </div>
        </Paper>
      </MuiModal>
    </>
  );
};

export default SessionTimeoutHandler;
